import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';
import { fieldBase, fieldSizes, type InputSize } from './input';

/**
 * Select — a custom listbox behind the shadcn composition
 * (Select / SelectTrigger / SelectValue / SelectContent / SelectItem).
 *
 * This REPLACES a native <select>. The native element was the right first call — free keyboard and
 * screen-reader behaviour, the OS picker on mobile, zero bytes — and the one thing it cannot do is
 * style an option row. That is now needed (checkmarks, tinted rows, disabled states that look
 * disabled), so the machinery it was giving us for free is machinery we now owe. Owed in full:
 *
 *   · ARIA. The trigger is `role=combobox` + `aria-expanded` + `aria-controls` + `aria-activedescendant`;
 *     the popover is `role=listbox`; each row is `role=option` + `aria-selected`. That quartet is
 *     what makes a div behave like a control to a screen reader, and three of the four are useless
 *     without the fourth.
 *   · Keyboard. ArrowUp/Down (and Home/End) move an ACTIVE row without committing it; Enter/Space
 *     commits; Escape closes and restores focus to the trigger; typeahead jumps to a prefix match.
 *     Tab is deliberately a commit-and-move, matching every native select on the platform.
 *   · Focus. Focus never enters the popover — it stays on the trigger, and the "active" row is
 *     pointed at with aria-activedescendant. This is the listbox pattern rather than a roving
 *     tabindex, and it is why closing the popover cannot strand focus on a node that just
 *     unmounted.
 *
 * WHY A PORTAL. The Select is used inside the orders table, whose cells are `overflow` clipped and
 * whose action column is `position: sticky` with a z-index. A popover rendered in place is either
 * clipped by the cell or painted under the next sticky column. So it renders to document.body and
 * is positioned against the trigger's measured rect, which puts it above every stacking context in
 * the table by construction.
 *
 *
 * HOW THE OPTIONS GET FOUND, and why it is not the obvious way.
 *
 * SelectItem and SelectValue render NOTHING. The rows — and the placeholder — are built by
 * SelectTrigger, which goes and finds them among its descendants. Two bugs lived in the original
 * implementation of that search, and both were visible on screen. They are worth keeping in mind
 * because this version inherits the fixes, not the bugs:
 *
 *   1. EMPTY DROPDOWNS. It matched children with `child.type === SelectContent` — an identity test
 *      on module-instance references — and only looked one level deep. The apps import through the
 *      `@most/ui` barrel while this file compares against its own local binding, so anything that
 *      gave the barrel a separate module instance made that `===` false and harvested nothing:
 *      every filter and the language picker rendered with no options at all. Fragments and mapped
 *      arrays defeated it too.
 *
 *      Fix: walk the whole subtree, and identify components by a static MARKER rather than by
 *      reference. A marker survives module duplication; `===` does not.
 *
 *   2. AN ACTION MENU THAT LIED. The placeholder used to be reported up from <SelectValue> via a
 *      useEffect, so on first paint the context's placeholder was still '' and the browser snapped
 *      the <select> to its first real option. On the orders board — where the status Select is an
 *      action menu bound to `value=""` — a brand-new order rendered as though it were already
 *      "В работе". The effect corrected it a tick later, but the DOM had already misreported the
 *      state of an order.
 *
 *      Fix: harvest the placeholder in the same synchronous walk as the items, so the very first
 *      render is correct. There is no placeholder state and no effect.
 */

const SELECT_ITEM = 'most.select.item';
const SELECT_VALUE = 'most.select.value';

const MARKER = Symbol.for('most.select.role');
type Marked = { [MARKER]?: string };

function roleOf(type: unknown): string | undefined {
  return (type as Marked | undefined)?.[MARKER];
}

type SelectContextValue = {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  /** Labelling props that landed on <Select> and need to reach the trigger — see below. */
  forwarded: Pick<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'id' | 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-invalid'
  >;
  /** Everything harvested from <Select>'s whole subtree — see the note on `collect`. */
  harvest: { items: SelectItemProps[]; placeholder?: string };
};

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelectContext(component: string): SelectContextValue {
  const ctx = React.useContext(SelectContext);
  if (!ctx) throw new Error(`<${component}> must be used inside <Select>`);
  return ctx;
}

export type SelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  children: React.ReactNode;

  /*
   * Labelling props are accepted HERE as well as on the trigger, and forwarded down to it.
   *
   * This is not API sugar, it is a trap being closed. <Field> labels its control by cloning `id`
   * and `aria-describedby` onto its child element — and when a Select is wrapped in a Field, that
   * child is <Select>, a context provider that renders no DOM. Those props would land on it and
   * evaporate: no error, no warning, just a <label htmlFor="locale"> pointing at an element that
   * does not exist, and a screen reader announcing an unlabeled combobox.
   *
   * The trigger's own props still win if both are given. Whichever way a caller composes it, the
   * label reaches the focusable element.
   */
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
};

export function Select({
  value,
  onValueChange,
  disabled,
  children,
  id,
  ...aria
}: SelectProps) {
  /*
   * The harvest happens HERE, over <Select>'s entire subtree, rather than inside SelectTrigger
   * over only the trigger's own children — and that is the whole point.
   *
   * Real shadcn/Radix puts <SelectContent> as a SIBLING of <SelectTrigger>:
   *
   *     <Select>
   *       <SelectTrigger><SelectValue placeholder="…" /></SelectTrigger>
   *       <SelectContent>…</SelectContent>          ← sibling, not child
   *     </Select>
   *
   * which is what anyone who has used shadcn will write from muscle memory. A trigger that only
   * searches its own children finds nothing there and renders an empty dropdown — silently, with
   * no error, which is precisely the failure this file's history is full of. Harvesting from the
   * provider means both layouts work: nest the content inside the trigger or hang it beside it,
   * and either way the items are found.
   */
  const harvest = React.useMemo(() => {
    const items: SelectItemProps[] = [];
    const found: { placeholder?: string } = {};
    collect(children, items, found);
    return { items, placeholder: found.placeholder };
  }, [children]);

  const ctx = React.useMemo<SelectContextValue>(
    () => ({
      value,
      onValueChange,
      disabled,
      harvest,
      forwarded: {
        id,
        'aria-label': aria['aria-label'],
        'aria-labelledby': aria['aria-labelledby'],
        'aria-describedby': aria['aria-describedby'],
        'aria-invalid': aria['aria-invalid'],
      },
    }),
    [
      value,
      onValueChange,
      disabled,
      harvest,
      id,
      aria['aria-label'],
      aria['aria-labelledby'],
      aria['aria-describedby'],
      aria['aria-invalid'],
    ],
  );
  return <SelectContext.Provider value={ctx}>{children}</SelectContext.Provider>;
}

/** Depth-first walk collecting the SelectItems and the placeholder. */
function collect(
  node: React.ReactNode,
  items: SelectItemProps[],
  found: { placeholder?: string },
): void {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;

    const role = roleOf(child.type);

    if (role === SELECT_ITEM) {
      items.push(child.props as SelectItemProps);
      return;
    }

    if (role === SELECT_VALUE) {
      const p = (child.props as { placeholder?: string }).placeholder;
      if (p) found.placeholder = p;
      return;
    }

    // SelectContent, a fragment, or any other wrapper: keep descending. Recursing through unknown
    // wrappers is deliberate — it is what lets a caller group options in a fragment or map them out
    // of an array without this component caring.
    const props = child.props as { children?: React.ReactNode };
    if (props?.children) collect(props.children, items, found);
  });
}

/**
 * The text a row matches on when the user types.
 *
 * `textValue` wins when given. Otherwise the row's children are flattened to text — and the
 * flattening is the point. A row is very often not a bare string:
 *
 *     <SelectItem value="p3">{name} {index}</SelectItem>   ← children is an ARRAY
 *     <SelectItem value="ru"><Flag /> Русский</SelectItem> ← children is an array with an element
 *
 * A naive `typeof children === 'string' ? children : ''` returns '' for both — and `''.startsWith(q)`
 * is TRUE for every query, so every row matches and typeahead lands on whatever it happens to try
 * first. That is not "typeahead does not work", it is "typeahead confidently jumps to the wrong
 * row", which is worse. Flatten instead, and fall back to a value that can never match rather than
 * one that always does.
 */
function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement(node)) {
    return textOf((node.props as { children?: React.ReactNode }).children);
  }
  return '';
}

function matchText(item: SelectItemProps): string {
  return (item.textValue ?? textOf(item.children)).trim().toLowerCase();
}

/** Where the popover goes. Measured from the trigger, in viewport coords, fixed-positioned. */
type Rect = { top: number; left: number; width: number; drop: 'down' | 'up'; maxHeight: number };

const GAP = 4;
const MAX_POPOVER = 288; // 18rem — about 8 rows, then it scrolls.

function measure(trigger: HTMLElement): Rect {
  const r = trigger.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - GAP - 8;
  const above = r.top - GAP - 8;

  // Flip up only when below genuinely cannot hold a usable list AND above is roomier. A popover
  // that flips for the sake of 4 extra pixels is a popover that jitters as the page scrolls.
  const drop: 'down' | 'up' = below < Math.min(MAX_POPOVER, 160) && above > below ? 'up' : 'down';
  const space = drop === 'down' ? below : above;

  return {
    top: drop === 'down' ? r.bottom + GAP : r.top - GAP,
    // Keep it on-screen. The popover matches the trigger's width, and the trigger can sit in the
    // pinned right-hand action column of a table that is itself scrolled — so left-aligning to it
    // blindly can hang the list off the right edge of the viewport.
    left: Math.max(8, Math.min(r.left, window.innerWidth - r.width - 8)),
    width: r.width,
    drop,
    maxHeight: Math.max(120, Math.min(MAX_POPOVER, space)),
  };
}

export type SelectTriggerProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onChange' | 'disabled' | 'children' | 'type'
> & {
  className?: string;
  children?: React.ReactNode;
  size?: InputSize;
};

/**
 * SelectTrigger renders the button, owns the open/active state, and renders the listbox popover.
 * The SelectItems anywhere beneath it become rows, so the shadcn composition reads the same.
 *
 * The caller's props land on the BUTTON, not on a wrapper: `id` and `aria-label` have to reach the
 * focusable element, or the <label> pointing at them addresses nothing.
 */
export function SelectTrigger({
  className,
  children: _children,
  size = 'md',
  ...own
}: SelectTriggerProps) {
  const { value, onValueChange, disabled, forwarded, harvest } = useSelectContext('SelectTrigger');

  // Anything <Field> cloned onto <Select> arrives via context; the trigger's own props win.
  const props = { ...forwarded, ...own };

  // Already collected by <Select> over the whole subtree — which INCLUDES this trigger's own
  // children, so both the nested and the sibling <SelectContent> layouts land here identically.
  // `children` is therefore declarative scaffolding only; the trigger never renders it.
  const items = harvest.items;
  const found = { placeholder: harvest.placeholder };

  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const [rect, setRect] = React.useState<Rect | null>(null);

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const rowRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const baseId = React.useId();
  const listId = `${baseId}-listbox`;
  const rowId = (i: number) => `${baseId}-opt-${i}`;

  const selectedIndex = items.findIndex((it) => it.value === value);
  const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined;

  const enabledIndexes = items
    .map((it, i) => (it.disabled ? -1 : i))
    .filter((i) => i >= 0);

  // setRect only when the geometry actually moved, so the per-frame tracker below can re-measure
  // freely without re-rendering the popover on frames where nothing changed.
  const reposition = React.useCallback(() => {
    if (!triggerRef.current) return;
    const next = measure(triggerRef.current);
    setRect((prev) =>
      prev &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.drop === next.drop &&
      prev.maxHeight === next.maxHeight
        ? prev
        : next,
    );
  }, []);

  const openList = React.useCallback(() => {
    if (disabled || items.length === 0) return;
    reposition();
    // Open ON the current selection when there is one, otherwise on the first row a user could
    // actually pick — landing the cursor on a disabled row is a dead first keypress.
    const onSelected = selected !== undefined && !selected.disabled;
    setActive(onSelected ? selectedIndex : (enabledIndexes[0] ?? -1));
    setOpen(true);
  }, [disabled, items.length, selected, selectedIndex, enabledIndexes, reposition]);

  const close = React.useCallback((refocus = true) => {
    setOpen(false);
    setActive(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const commit = React.useCallback(
    (i: number) => {
      const item = items[i];
      if (!item || item.disabled) return;
      onValueChange(item.value);
      close();
    },
    [items, onValueChange, close],
  );

  /* Reposition on scroll/resize while open. A fixed-positioned popover measured against a trigger
     that then scrolls away would otherwise hang in space — and the trigger DOES scroll: it lives in
     a table body that scrolls under a sticky header. `capture` catches scrolls on the table's own
     overflow container, not just the window. */
  React.useEffect(() => {
    if (!open) return;
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, reposition]);

  /* Follow the trigger while it MOVES without scrolling. Two things do that, and neither fires a
     scroll or resize event, so the effect above misses both:

       1. Content reflows ABOVE the trigger after the list is already open. On the notifications
          board the "N dead" banner is gated on an async dashboard query; when it resolves it is
          inserted above the toolbar and shoves the whole toolbar — and this trigger — down by a
          banner's height. A skeleton→rows swap moves it too. A popover measured before the reflow
          is left orphaned in mid-air where the trigger used to be — the exact "detached menu
          floating on the page" bug. A ResizeObserver on <html> catches every such reflow.

       2. The page is still animating IN. PageTransition crossfades each screen with a settling
          `translateY`, driven by framer-motion per frame — a transform, not a layout change, so no
          resize fires and getBoundingClientRect keeps reporting a moving top. Open the filter in
          that ~200 ms window and the popover pins to a position the trigger then slides away from.
          A short rAF follow re-measures each frame until the rect holds still twice running, then
          stops so we are not spinning a frame loop for a stationary popover. */
  React.useEffect(() => {
    if (!open) return;

    const ro = new ResizeObserver(() => reposition());
    ro.observe(document.documentElement);

    let raf = 0;
    let lastKey = '';
    let settled = 0;
    const follow = () => {
      const el = triggerRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        const key = `${r.top}:${r.left}:${r.width}`;
        if (key === lastKey) {
          // Two identical frames in a row: the entry transition has settled, stop following.
          if ((settled += 1) >= 2) return;
        } else {
          settled = 0;
          lastKey = key;
          reposition();
        }
      }
      raf = requestAnimationFrame(follow);
    };
    raf = requestAnimationFrame(follow);

    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [open, reposition]);

  /* Dismiss on an outside pointerdown. `pointerdown` rather than `click`, so the list closes on
     press rather than on release — a click that starts inside and releases outside is not a
     dismissal, and the reverse feels laggy. */
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || listRef.current?.contains(t)) return;
      close(false); // Not our focus to take — the user is clicking somewhere else entirely.
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, close]);

  /* Keep the active row in view. `block: 'nearest'` scrolls the minimum needed, so arrowing through
     a long list creeps rather than jumping the list under the cursor. */
  React.useEffect(() => {
    if (!open || active < 0) return;
    rowRefs.current[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  /* Typeahead. Native selects have it, so a custom one that drops it is a regression a keyboard
     user feels immediately: 25 producers in a filter, and no way to reach "Ýyldyz" but 24 arrows.
     The buffer clears after a beat so "sa" then later "s" are separate searches. */
  const typed = React.useRef('');
  const typedTimer = React.useRef<ReturnType<typeof setTimeout>>();

  const typeahead = React.useCallback(
    (ch: string) => {
      clearTimeout(typedTimer.current);
      typed.current += ch.toLowerCase();
      typedTimer.current = setTimeout(() => {
        typed.current = '';
      }, 600);

      const q = typed.current;

      // Search from AFTER the cursor and wrap, so repeatedly typing "n" cycles the n-words rather
      // than sticking on the first one.
      const n = items.length;
      const from = active >= 0 ? active : -1;
      for (let k = 1; k <= n; k += 1) {
        const i = (from + k + n) % n;
        const it = items[i];
        // An empty label must never match — see `textOf`. `q` is non-empty here, so a row with no
        // extractable text simply cannot be reached by typing, which is the correct failure.
        if (it && !it.disabled && matchText(it) !== '' && matchText(it).startsWith(q)) {
          setActive(i);
          return;
        }
      }
    },
    [items, active],
  );

  React.useEffect(() => () => clearTimeout(typedTimer.current), []);

  /** Move the cursor to the next/previous ENABLED row. Clamps at the ends — no wrap, matching a
      native select, where holding Down at the bottom does not teleport you to the top. */
  const step = (dir: 1 | -1) => {
    const last = enabledIndexes.length - 1;
    if (last < 0) return;
    const pos = enabledIndexes.indexOf(active);
    // From nowhere, Down lands on the first enabled row and Up on the last.
    const nextPos = pos === -1 ? (dir === 1 ? 0 : last) : Math.min(Math.max(pos + dir, 0), last);
    const next = enabledIndexes[nextPos];
    if (next !== undefined) setActive(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    // Closed: these keys open the list. Everything else falls through to the browser.
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openList();
      } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // Typing on a closed select opens it and jumps — same as native.
        e.preventDefault();
        openList();
        typeahead(e.key);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        step(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        step(-1);
        break;
      case 'Home': {
        e.preventDefault();
        const first = enabledIndexes[0];
        if (first !== undefined) setActive(first);
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = enabledIndexes[enabledIndexes.length - 1];
        if (last !== undefined) setActive(last);
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        commit(active);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        // Commit and let focus move on, which is what every native select does. Closing WITHOUT
        // committing here would silently discard a selection the user has already arrowed to and
        // can see highlighted.
        if (active >= 0) commit(active);
        else close(false);
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          typeahead(e.key);
        }
    }
  };

  const placeholder = found.placeholder ?? '';
  const hasValue = selected !== undefined;

  const popover =
    open && rect
      ? createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-labelledby={props.id}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              maxHeight: rect.maxHeight,
              ...(rect.drop === 'down'
                ? { top: rect.top }
                : { bottom: window.innerHeight - rect.top }),
            }}
            className={cn(
              'z-50 overflow-y-auto rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg',
              rect.drop === 'down' ? 'animate-in fade-in-0 zoom-in-95' : 'animate-in fade-in-0',
            )}
          >
            {items.map((item, i) => {
              const isSelected = item.value === value;
              const isActive = i === active;
              return (
                <div
                  key={item.value}
                  id={rowId(i)}
                  ref={(el) => {
                    rowRefs.current[i] = el;
                  }}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={item.disabled || undefined}
                  // Mouse and keyboard drive the SAME `active` state, so the highlight never
                  // appears in two places at once — which is what happens when hover is a CSS
                  // :hover and the keyboard cursor is a class.
                  onPointerEnter={() => !item.disabled && setActive(i)}
                  onPointerDown={(e) => {
                    e.preventDefault(); // Don't blur the trigger — focus must stay put.
                    commit(i);
                  }}
                  className={cn(
                    'relative flex cursor-pointer select-none items-center gap-2 rounded-md py-1.5 pl-8 pr-2 text-sm outline-none',
                    isActive && !item.disabled && 'bg-accent text-accent-foreground',
                    item.disabled && 'pointer-events-none opacity-50',
                  )}
                >
                  {isSelected && (
                    <svg
                      aria-hidden
                      viewBox="0 0 16 16"
                      fill="none"
                      className="absolute left-2 h-4 w-4"
                    >
                      <path
                        d="M3.5 8.5l3 3 6-6.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  <span className="truncate">{item.children}</span>
                </div>
              );
            })}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        {...props}
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? rowId(active) : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        className={cn(
          fieldBase,
          fieldSizes[size],
          'flex items-center justify-between gap-2 text-left',
          // The placeholder must LOOK like a placeholder, or an unset filter reads as a real value.
          !hasValue && 'text-muted-foreground',
          className,
        )}
      >
        <span className="truncate">{hasValue ? selected.children : placeholder}</span>
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            open && 'rotate-180',
          )}
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </button>
      {popover}
    </>
  );
}

/** Declarative only — SelectTrigger reads the placeholder off this during render. */
export function SelectValue(_props: { placeholder?: string }): React.ReactElement | null {
  return null;
}

/** A container whose descendants SelectTrigger reads. Renders nothing on its own. */
export function SelectContent({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export type SelectItemProps = {
  value: string;
  disabled?: boolean;
  children: React.ReactNode;
  /**
   * What typeahead matches on when `children` is not a plain string (an icon + label, a badge).
   * Without it, a row whose children are JSX is simply unreachable by typing.
   */
  textValue?: string;
};

/** Declarative only — SelectTrigger turns these into rows. */
export function SelectItem(_props: SelectItemProps): React.ReactElement | null {
  return null;
}

// The markers the collector matches on. Static properties survive module duplication, which is
// exactly what reference equality did not.
Object.assign(SelectValue, { [MARKER]: SELECT_VALUE });
Object.assign(SelectItem, { [MARKER]: SELECT_ITEM });
